/**
 * What a refused progress-button press tells the user, next to the button it refused.
 *
 * Prod clicks (Oct 2026): one user in five pressed the same button three or more times within
 * ten seconds. A refused press looked like nothing had happened: a corner toast, a spinner that
 * went back to the same label, or "Continued ✓" for work that never started. A refusal now
 * carries the server's reason and at most one control that fixes it; the bar shows both in place.
 */
import { normalizeApiError, type ApiError } from '../../services/apiError';
import { getCreditShortfall } from '../../utils/creditGate';
import { formatPhaseCredits } from '../../utils/phaseBuildPresentation';
import { planningCreditRefusalFrom } from '../ChatBox/planningCreditRefusalError';
import { ACTION_STALE_CODE, EXPLAINED_REFUSAL_CODES } from '../planningActionsRefresh';

/** The one control that fixes a refused press. */
export type PressFixKind =
  | 'add_credits'
  | 'upgrade'
  | 'refresh_actions'
  | 'retry'
  | 'resolve_tasks'
  | 'prerequisite';

export interface PressFix {
  kind: PressFixKind;
  label: string;
  /** add_credits: the shortfall the purchase form starts from. */
  credits?: number;
  /** prerequisite: the gate controls that run the step (pressed on screen by label). */
  labels?: string[];
}

/** One earlier step a press waits for, listed in order under the refusal. */
export interface PressStep {
  key: string;
  summary: string;
}

export interface PressRefusal {
  /** Machine-readable reason: insufficient_credits, ACTION_STALE, TASK_PLAN_MISSING, … */
  code: string;
  /** Why nothing started, in words the user can act on. */
  message: string;
  fix?: PressFix;
  /** What must happen first, in order; the fix does the first step that has a control. */
  steps?: PressStep[];
}

export const ADD_CREDITS_FIX_LABEL = 'Add Credits';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const numberField = (record: Record<string, unknown>, ...keys: string[]): number | undefined => {
  const value = keys.map((key) => record[key]).find((item) => typeof item === 'number' && Number.isFinite(item));

  return value as number | undefined;
};

// The need rounds up and the balance rounds down, as the start quote's note does, so it never reads
// "needs 0.2 · you have 0.2".
const needs = (value: number): string => formatPhaseCredits(Math.ceil(value * 100 - 1e-9) / 100);
const have = (value: number): string => formatPhaseCredits(Math.floor(Math.max(0, value) * 100 + 1e-9) / 100);

/** "Needs 35 credits · you have 3.8." when the price is known, else "Needs 31.2 more credits." */
export const creditShortfallMessage = (
  shortfall: number,
  required?: number | null,
  available?: number | null,
): string => (
  typeof required === 'number' && typeof available === 'number' && required > 0
    ? `Needs ${needs(required)} credits · you have ${have(available)}.`
    : `Needs ${needs(shortfall)} more credits.`
);

/** A press the balance cannot pay for, with Add Credits preselecting the shortfall. */
export const creditRefusal = (
  shortfall: number,
  required?: number | null,
  available?: number | null,
): PressRefusal => ({
  code: 'insufficient_credits',
  message: creditShortfallMessage(shortfall, required, available),
  fix: { kind: 'add_credits', label: ADD_CREDITS_FIX_LABEL, credits: shortfall },
});

const planningCreditRefusal = (error: unknown): PressRefusal | null => {
  const refusal = planningCreditRefusalFrom(error);

  if (!refusal) return null;
  const fix: PressFix | undefined = refusal.add_credits === false
    ? (refusal.upgrade_available ? { kind: 'upgrade', label: 'Upgrade' } : undefined)
    : { kind: 'add_credits', label: ADD_CREDITS_FIX_LABEL, credits: refusal.shortfall };

  return { code: 'insufficient_credits', message: refusal.message, ...(fix ? { fix } : {}) };
};

/** A plain 402 (a build, a task): its detail carries the price and the balance under either naming. */
const paymentRefusal = (normalized: ApiError): PressRefusal => {
  const detail = isRecord(normalized.detail) ? normalized.detail : {};
  const required = numberField(detail, 'required_credits', 'required');
  const available = numberField(detail, 'available_credits', 'available', 'balance');
  const shortfall = getCreditShortfall({
    shortfall: numberField(detail, 'credit_shortfall', 'shortfall'), required, balance: available,
  });

  if (shortfall === undefined) {
    return { code: 'insufficient_credits', message: normalized.message, fix: { kind: 'add_credits', label: ADD_CREDITS_FIX_LABEL } };
  }

  return creditRefusal(shortfall, required, available);
};

const BUILD_RESOLVE_CODES = new Set(['PHASE_NEEDS_ATTENTION', 'PHASE_DEPENDENCY_BLOCKED', 'PHASE_TASK_NOT_READY']);

/** The fix a refusal's code implies, when one control can fix it. */
const fixForCode = (normalized: ApiError): PressFix | undefined => {
  if (normalized.code === ACTION_STALE_CODE) return { kind: 'refresh_actions', label: 'Refresh actions' };
  if (BUILD_RESOLVE_CODES.has(normalized.code)) return { kind: 'resolve_tasks', label: 'Open Phase Tasks' };
  if (normalized.status === 429 && normalized.quotaQueue) return { kind: 'upgrade', label: 'Upgrade' };
  if (normalized.kind === 'network' || normalized.kind === 'timeout' || (normalized.status ?? 0) >= 500) {
    return { kind: 'retry', label: 'Try Again' };
  }

  return undefined;
};

/** A refusal the server explained (any answer with a status), else the caller's words. */
const explainedRefusal = (normalized: ApiError, fallback: string): PressRefusal => {
  const explained = EXPLAINED_REFUSAL_CODES.has(normalized.code) || normalized.kind !== 'unknown';
  const fix = fixForCode(normalized);

  return { code: normalized.code, message: explained ? normalized.message : fallback, ...(fix ? { fix } : {}) };
};

/** Turn any failed press into the reason and the one fix the bar shows; null for a cancelled request. */
export const pressRefusalFromError = (error: unknown, fallback: string): PressRefusal | null => {
  const normalized = normalizeApiError(error);

  if (normalized.kind === 'cancelled') return null;
  const payment = normalized.status === 402 || normalized.code === 'INSUFFICIENT_CREDITS';

  return payment
    ? (planningCreditRefusal(error) ?? paymentRefusal(normalized))
    : explainedRefusal(normalized, fallback);
};

/** Two refusals say the same thing: a repeated press of a refused button updates nothing on screen. */
export const sameRefusal = (left: PressRefusal | null, right: PressRefusal | null): boolean => (
  left === right || Boolean(left && right && left.code === right.code && left.message === right.message)
);
